import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable, TextInput, Modal } from 'react-native';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  Users,
  UserPlus,
  Crown,
  Briefcase,
  MessageSquare,
  X,
  Trash2,
  Mail,
  Shield,
  ChevronLeft,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { StartupTeamMember, TeamMemberRole } from '@/lib/state/inverio-store';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAlert } from '@/components/AlertDialog';

// Breadcrumbs for navigation
const BREADCRUMBS: Breadcrumb[] = [
  { label: 'Company Profile', href: '/(startup)/company' },
  { label: 'Team Management' },
];

const getRoleConfig = (colors: ReturnType<typeof useThemeColors>): Record<TeamMemberRole, { label: string; color: string; icon: typeof Crown }> => ({
  founder: { label: 'Founder', color: colors.warning, icon: Crown },
  'co-founder': { label: 'Co-Founder', color: colors.primary[400], icon: Crown },
  investor_relations: { label: 'Investor Relations', color: colors.success, icon: Briefcase },
  other: { label: 'Team Member', color: colors.dark[400], icon: Users },
});

const roleOptions: { value: TeamMemberRole; label: string }[] = [
  { value: 'co-founder', label: 'Co-Founder' },
  { value: 'investor_relations', label: 'Investor Relations' },
  { value: 'other', label: 'Other Team Member' },
];

export default function TeamManagementScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const { error, success, confirmDestructive } = useAlert();
  const roleConfig = getRoleConfig(colors);
  const teamMembers = useProfileStore((s) => s.teamMembers);
  const addTeamMember = useProfileStore((s) => s.addTeamMember);
  const removeTeamMember = useProfileStore((s) => s.removeTeamMember);
  const startupProfile = useProfileStore((s) => s.startupProfile);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberTitle, setNewMemberTitle] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<TeamMemberRole>('other');

  // Current user is founder (first member or profile owner)
  const currentUserIsFounder = true; // In real app, check against logged-in user

  const handleAddMember = () => {
    if (!newMemberName.trim() || !newMemberEmail.trim()) {
      error('Missing Information', 'Please enter name and email for the team member.');
      return;
    }

    const newMember: StartupTeamMember = {
      id: `member-${Date.now()}`,
      name: newMemberName.trim(),
      email: newMemberEmail.trim(),
      role: newMemberRole,
      title: newMemberTitle.trim() || roleConfig[newMemberRole].label,
      addedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      addedBy: 'founder',
    };

    addTeamMember(newMember);
    setShowAddModal(false);
    resetForm();
    success('Team Member Added', `${newMember.name} has been added to your team.`);
  };

  const handleRemoveMember = async (member: StartupTeamMember) => {
    if (member.role === 'founder') {
      error('Cannot Remove', 'The founder cannot be removed from the team.');
      return;
    }

    if (await confirmDestructive('Remove Team Member', `Are you sure you want to remove ${member.name} from the team?`, 'Remove')) {
      removeTeamMember(member.id);
      success('Removed', `${member.name} has been removed from the team.`);
    }
  };

  const resetForm = () => {
    setNewMemberName('');
    setNewMemberEmail('');
    setNewMemberTitle('');
    setNewMemberRole('other');
  };

  // Mock founder as first team member if none exist
  const displayMembers: StartupTeamMember[] = teamMembers.length > 0
    ? teamMembers
    : [{
        id: 'founder-1',
        name: startupProfile?.name ? `${startupProfile.name} Team` : 'Founder',
        email: 'founder@startup.com',
        role: 'founder',
        title: 'Founder & CEO',
        addedDate: 'Original',
        addedBy: 'system',
      }];

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
        {/* Header - Only show on mobile */}
        {phoneShell && (
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="px-5 pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <View className="flex-row items-center gap-4">
                <AnimatedPressable
                  onPress={() => router.back()}
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.surface.tertiary }}
                >
                  <ChevronLeft size={24} color={colors.text.primary} />
                </AnimatedPressable>
                <View>
                  <Text style={{ color: colors.text.secondary }} className="text-sm">Settings</Text>
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-1">Team Members</Text>
                </View>
              </View>
              {currentUserIsFounder && (
                <AnimatedPressable
                  onPress={() => setShowAddModal(true)}
                  className="w-12 h-12 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <UserPlus size={22} color="white" />
                </AnimatedPressable>
              )}
            </View>
          </Animated.View>
        )}

        {/* Web Header with Add Button */}
        {isWeb && currentUserIsFounder && (
          <View className="px-5 pt-4 pb-4 flex-row justify-end">
            <AnimatedPressable
              onPress={() => setShowAddModal(true)}
              className="flex-row items-center gap-2 px-4 py-2 rounded-lg"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <UserPlus size={18} color="white" />
              <Text className="text-white font-semibold">Add Member</Text>
            </AnimatedPressable>
          </View>
        )}

          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Info Banner */}
            <Animated.View
              entering={FadeInUp.delay(150).duration(500)}
              className="px-5 mb-6"
            >
              <View
                className="rounded-2xl p-4"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <View className="flex-row items-center gap-3">
                  <Shield size={20} color={colors.primary[600]} />
                  <View className="flex-1">
                    <Text style={{ color: colors.primary[700] }} className="font-semibold">
                      Founder Controls
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-xs mt-0.5">
                      Only the founder can add or remove team members. All members can participate in investor discussions.
                    </Text>
                  </View>
                </View>
              </View>
            </Animated.View>

            {/* Stats */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mb-6"
            >
              <View className="flex-row gap-3">
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <Users size={20} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-2">{displayMembers.length}</Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs">Team Members</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <MessageSquare size={20} color={colors.success} />
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-2">All</Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs">Can Message Investors</Text>
                </View>
              </View>
            </Animated.View>

            {/* Team List */}
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="px-5 pb-8"
            >
              <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-4">Your Team</Text>
              <View className="gap-3">
                {displayMembers.map((member, index) => {
                  const role = roleConfig[member.role];
                  const RoleIcon = role.icon;

                  return (
                    <Animated.View
                      key={member.id}
                      entering={FadeIn.delay(300 + index * 50).duration(300)}
                    >
                      <View
                        className="rounded-2xl overflow-hidden"
                        style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                      >
                        <View className="p-4">
                          <View className="flex-row items-center justify-between">
                            <View className="flex-row items-center gap-3 flex-1">
                              <View
                                className="w-12 h-12 rounded-full items-center justify-center"
                                style={{ backgroundColor: `${role.color}20` }}
                              >
                                <Text style={{ color: role.color }} className="text-lg font-bold">
                                  {member.name.charAt(0).toUpperCase()}
                                </Text>
                              </View>
                              <View className="flex-1">
                                <View className="flex-row items-center gap-2">
                                  <Text style={{ color: colors.text.primary }} className="font-semibold">{member.name}</Text>
                                  {member.role === 'founder' && (
                                    <Crown size={14} color={colors.warning} />
                                  )}
                                </View>
                                <Text style={{ color: colors.text.secondary }} className="text-sm">
                                  {member.title}
                                </Text>
                                <View className="flex-row items-center gap-2 mt-1">
                                  <View
                                    className="px-2 py-0.5 rounded"
                                    style={{ backgroundColor: `${role.color}20` }}
                                  >
                                    <Text style={{ color: role.color }} className="text-xs font-medium">
                                      {role.label}
                                    </Text>
                                  </View>
                                </View>
                              </View>
                            </View>

                            {currentUserIsFounder && member.role !== 'founder' && (
                              <AnimatedPressable
                                onPress={() => handleRemoveMember(member)}
                                className="w-10 h-10 rounded-full items-center justify-center"
                                style={{ backgroundColor: colors.red.light }}
                              >
                                <Trash2 size={18} color={colors.error} />
                              </AnimatedPressable>
                            )}
                          </View>

                          {/* Member Details */}
                          <View
                            className="flex-row items-center gap-2 mt-3 pt-3 border-t"
                            style={{ borderColor: colors.border.light }}
                          >
                            <Mail size={14} color={colors.text.tertiary} />
                            {/* `flex-1` + single line: an address like
                                alexandra.rodriguez@example-company.com is ~250pt
                                at this size, and with no way to shrink it pushed
                                itself and the "Added" suffix out of the card on
                                every phone width. */}
                            <Text
                              style={{ color: colors.text.secondary }}
                              className="text-sm flex-1"
                              numberOfLines={1}
                            >
                              {member.email}
                            </Text>
                            {member.addedDate !== 'Original' && (
                              <>
                                <Text style={{ color: colors.text.tertiary }} className="text-xs shrink-0">•</Text>
                                <Text
                                  style={{ color: colors.text.tertiary }}
                                  className="text-xs shrink-0"
                                  numberOfLines={1}
                                >
                                  Added {member.addedDate}
                                </Text>
                              </>
                            )}
                          </View>
                        </View>
                      </View>
                    </Animated.View>
                  );
                })}
              </View>
            </Animated.View>

            {/* Permissions Info */}
            <Animated.View
              entering={FadeInUp.delay(400).duration(500)}
              className="px-5 pb-8"
            >
              <View
                className="rounded-2xl p-4"
                style={{ backgroundColor: `${colors.dark[700]}60` }}
              >
                <Text style={{ color: colors.dark[200] }} className="text-sm font-medium mb-3">
                  Team Permissions
                </Text>
                <View className="gap-2">
                  <View className="flex-row items-center gap-2">
                    <Crown size={14} color={colors.warning} />
                    <Text style={{ color: colors.dark[400] }} className="text-xs">
                      <Text style={{ color: colors.warning }}>Founder:</Text> Full access, add/remove members
                    </Text>
                  </View>
                  <View className="flex-row items-center gap-2">
                    <Crown size={14} color={colors.primary[400]} />
                    <Text style={{ color: colors.dark[400] }} className="text-xs">
                      <Text style={{ color: colors.primary[400] }}>Co-Founder:</Text> Full access to discussions
                    </Text>
                  </View>
                  <View className="flex-row items-center gap-2">
                    <Briefcase size={14} color={colors.success} />
                    <Text style={{ color: colors.dark[400] }} className="text-xs">
                      <Text style={{ color: colors.success }}>Investor Relations:</Text> Manage investor communications
                    </Text>
                  </View>
                  <View className="flex-row items-center gap-2">
                    <Users size={14} color={colors.dark[400]} />
                    <Text style={{ color: colors.dark[400] }} className="text-xs">
                      <Text className="text-white">Other:</Text> View and respond to discussions
                    </Text>
                  </View>
                </View>
              </View>
            </Animated.View>
          </ScrollView>
        </SafeAreaView>

      {/* Add Team Member Modal */}
      <Modal
        visible={showAddModal}
        animationType="slide"
        transparent
        onRequestClose={() => setShowAddModal(false)}
      >
        <View className={isWeb ? "flex-1 justify-center items-center" : "flex-1 justify-end"} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <View
            className={isWeb ? "rounded-2xl p-5" : "rounded-t-3xl p-5"}
            style={{ backgroundColor: colors.dark[800], maxHeight: '85%', ...(isWeb && { width: '100%', maxWidth: 480 }) }}
          >
            <View className="flex-row items-center justify-between mb-6">
              <Text className="text-white text-xl font-bold">Add Team Member</Text>
              <Pressable
                onPress={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.dark[700] }}
              >
                <X size={20} color={colors.dark[400]} />
              </Pressable>
            </View>

            {/* Keyboard-aware: this is a bottom sheet on mobile, so the fields
                sit closest to the keyboard and were the first to disappear
                behind it. */}
            <KeyboardAwareBody>
              {/* Name */}
              <View className="mb-4">
                <Text style={{ color: colors.dark[300] }} className="text-sm mb-2 font-medium">
                  Full Name *
                </Text>
                <View
                  className="rounded-xl px-4"
                  style={{ backgroundColor: `${colors.dark[700]}80` }}
                >
                  <TextInput
                    className="text-white py-4 text-base"
                    placeholder="Enter name"
                    placeholderTextColor={colors.dark[500]}
                    value={newMemberName}
                    onChangeText={setNewMemberName}
                  />
                </View>
              </View>

              {/* Email */}
              <View className="mb-4">
                <Text style={{ color: colors.dark[300] }} className="text-sm mb-2 font-medium">
                  Email Address *
                </Text>
                <View
                  className="rounded-xl px-4"
                  style={{ backgroundColor: `${colors.dark[700]}80` }}
                >
                  <TextInput
                    className="text-white py-4 text-base"
                    placeholder="Enter email"
                    placeholderTextColor={colors.dark[500]}
                    value={newMemberEmail}
                    onChangeText={setNewMemberEmail}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                </View>
              </View>

              {/* Title */}
              <View className="mb-4">
                <Text style={{ color: colors.dark[300] }} className="text-sm mb-2 font-medium">
                  Job Title
                </Text>
                <View
                  className="rounded-xl px-4"
                  style={{ backgroundColor: `${colors.dark[700]}80` }}
                >
                  <TextInput
                    className="text-white py-4 text-base"
                    placeholder="e.g., Head of Investor Relations"
                    placeholderTextColor={colors.dark[500]}
                    value={newMemberTitle}
                    onChangeText={setNewMemberTitle}
                  />
                </View>
              </View>

              {/* Role Selection */}
              <View className="mb-6">
                <Text style={{ color: colors.dark[300] }} className="text-sm mb-2 font-medium">
                  Role
                </Text>
                <View className="gap-2">
                  {roleOptions.map((option) => {
                    const roleInfo = roleConfig[option.value];
                    return (
                      <AnimatedPressable
                        key={option.value}
                        onPress={() => setNewMemberRole(option.value)}
                        className="flex-row items-center gap-3 p-4 rounded-xl"
                        style={{
                          backgroundColor: newMemberRole === option.value
                            ? `${roleInfo.color}20`
                            : `${colors.dark[700]}80`,
                          borderWidth: newMemberRole === option.value ? 2 : 0,
                          borderColor: roleInfo.color,
                        }}
                      >
                        <View
                          className="w-10 h-10 rounded-full items-center justify-center"
                          style={{ backgroundColor: `${roleInfo.color}20` }}
                        >
                          <roleInfo.icon size={20} color={roleInfo.color} />
                        </View>
                        <Text className="text-white font-medium flex-1">{option.label}</Text>
                        {newMemberRole === option.value && (
                          <View
                            className="w-6 h-6 rounded-full items-center justify-center"
                            style={{ backgroundColor: roleInfo.color }}
                          >
                            <Text className="text-white text-xs font-bold">✓</Text>
                          </View>
                        )}
                      </AnimatedPressable>
                    );
                  })}
                </View>
              </View>

              {/* Add Button */}
              <AnimatedPressable
                onPress={handleAddMember}
                disabled={!newMemberName.trim() || !newMemberEmail.trim()}
                className="rounded-xl py-4 items-center mb-4"
                style={{
                  backgroundColor: newMemberName.trim() && newMemberEmail.trim()
                    ? colors.primary[600]
                    : colors.dark[600],
                }}
              >
                <Text
                  className="font-semibold"
                  style={{
                    color: newMemberName.trim() && newMemberEmail.trim() ? 'white' : colors.dark[500],
                  }}
                >
                  Add Team Member
                </Text>
              </AnimatedPressable>

              <Text style={{ color: colors.dark[500] }} className="text-xs text-center mb-4">
                Team members will receive an email invitation to join your startup account.
              </Text>
            </KeyboardAwareBody>
          </View>
        </View>
      </Modal>
    </View>
  );

  // Wrap with AppLayout for web
  if (isWeb) {
    return (
      <AppLayout title="Team Management" breadcrumbs={BREADCRUMBS}>
        {content}
      </AppLayout>
    );
  }

  return content;
}
